import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    Home as HomeIcon,
    DirectionsTransit as DirectionsTransitIcon,
    LocationCity as LocationCityIcon,
    AttachMoney as AttachMoneyIcon,
    Security as SecurityIcon,
    CheckCircle as CheckCircleIcon,
    Star as StarIcon,
    Group as GroupIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: '東京都内でルームシェアをする魅力とは' },
    { id: 'area-selection', label: 'エリア選びで重視したい5つのポイント' },
    { id: 'area-list', label: 'おすすめルームシェアエリア10選' },
    { id: 'comparison', label: '主要エリア徹底比較表' },
    { id: 'advice', label: '専門家のアドバイスと注意点' },
    { id: 'summary', label: 'まとめ' }
];

const areaData = [
    {
        id: 'shimokitazawa',
        name: '下北沢',
        icon: <LocationCityIcon color="primary" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '新宿・渋谷まで電車で10分圏内、2路線利用可能' },
            { icon: <HomeIcon />, label: 'カフェや古着屋、カルチャー色の強い商店街' },
            { icon: <SecurityIcon />, label: '治安も比較的良好、女性にも人気' },
            { icon: <AttachMoneyIcon />, label: '家賃相場はやや高めだが、住みやすさ抜群' }
        ],
        tags: ['カルチャー', 'アクセス', '住みやすさ']
    },
    {
        id: 'nakameguro',
        name: '中目黒',
        icon: <LocationCityIcon color="secondary" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '東急東横線・日比谷線で渋谷・恵比寿に直結' },
            { icon: <HomeIcon />, label: '目黒川沿いの自然とおしゃれな飲食店' },
            { icon: <SecurityIcon />, label: '治安良好、落ち着いた大人の街' },
            { icon: <AttachMoneyIcon />, label: '家賃はやや高いが快適なシェア生活が可能' }
        ],
        tags: ['おしゃれ', '自然', '治安']
    },
    {
        id: 'kitasenju',
        name: '北千住',
        icon: <LocationCityIcon color="success" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '5路線利用可、都心アクセス抜群' },
            { icon: <HomeIcon />, label: '下町情緒と新しい再開発が共存' },
            { icon: <AttachMoneyIcon />, label: '家賃相場は都心より抑えめ' },
            { icon: <SecurityIcon />, label: '治安はエリアによって差あり' }
        ],
        tags: ['下町', '利便性', '家賃']
    },
    {
        id: 'nerima',
        name: '練馬',
        icon: <LocationCityIcon color="info" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '都心まで20分圏内、西武池袋線・大江戸線利用可' },
            { icon: <HomeIcon />, label: '自然や公園が多く、住環境良好' },
            { icon: <AttachMoneyIcon />, label: '家賃相場はリーズナブル' },
            { icon: <SecurityIcon />, label: '治安も安定、初めての一人暮らしにも最適' }
        ],
        tags: ['静か', '自然', 'コスパ']
    },
    {
        id: 'koenji',
        name: '高円寺',
        icon: <LocationCityIcon color="warning" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '中央線で新宿まで約7分、アクセス良好' },
            { icon: <HomeIcon />, label: 'サブカル感あふれる街並みと個性的な店' },
            { icon: <AttachMoneyIcon />, label: '家賃相場は都心よりも抑えめ' },
            { icon: <SecurityIcon />, label: '治安も比較的良いが、夜は注意' }
        ],
        tags: ['サブカル', '活気', 'アクセス']
    },
    {
        id: 'ebisu',
        name: '恵比寿',
        icon: <LocationCityIcon color="error" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '山手線・埼京線で渋谷・新宿・品川直結' },
            { icon: <HomeIcon />, label: '洗練された飲食店と買い物環境' },
            { icon: <AttachMoneyIcon />, label: '家賃は高めだが利便性と快適さ抜群' },
            { icon: <SecurityIcon />, label: '治安良好、女性にも安心' }
        ],
        tags: ['都会', '利便性', 'グルメ']
    },
    {
        id: 'asakusa',
        name: '浅草',
        icon: <LocationCityIcon color="primary" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '銀座線・都営浅草線など複数路線' },
            { icon: <HomeIcon />, label: '下町情緒と観光名所が身近' },
            { icon: <AttachMoneyIcon />, label: '家賃相場は比較的リーズナブル' },
            { icon: <SecurityIcon />, label: '観光地ゆえに人通りが多く安心' }
        ],
        tags: ['観光', '下町', 'アクセス']
    },
    {
        id: 'meguro',
        name: '目黒',
        icon: <LocationCityIcon color="secondary" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '山手線・南北線で都心主要地へスムーズ' },
            { icon: <HomeIcon />, label: '緑豊かな公園や落ち着いた街並み' },
            { icon: <AttachMoneyIcon />, label: '家賃は高めだが高級感あり' },
            { icon: <SecurityIcon />, label: '治安は非常に良い' }
        ],
        tags: ['高級', '緑', '快適']
    },
    {
        id: 'ogikubo',
        name: '荻窪',
        icon: <LocationCityIcon color="info" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '中央線・丸ノ内線利用で新宿・東京直結' },
            { icon: <HomeIcon />, label: '商店街と住宅街のバランスが良い' },
            { icon: <AttachMoneyIcon />, label: '家賃相場は中央線沿線では抑えめ' },
            { icon: <SecurityIcon />, label: '治安良好、落ち着いた層に人気' }
        ],
        tags: ['静か', '便利', 'バランス']
    },
    {
        id: 'kameido',
        name: '亀戸',
        icon: <LocationCityIcon color="success" />,
        points: [
            { icon: <DirectionsTransitIcon />, label: '総武線・東武亀戸線で都心へスムーズ' },
            { icon: <HomeIcon />, label: '庶民的な雰囲気と買い物のしやすさ' },
            { icon: <AttachMoneyIcon />, label: '家賃相場は比較的リーズナブル' },
            { icon: <SecurityIcon />, label: '治安は比較的安定' }
        ],
        tags: ['庶民的', 'コスパ', '生活感']
    }
];

const areaComparison = [
    { area: '下北沢', access: '◎', environment: '◎', safety: '○', rent: '△', recommend: '若者・女性' },
    { area: '中目黒', access: '◎', environment: '◎', safety: '◎', rent: '△', recommend: '大人・カップル' },
    { area: '北千住', access: '◎', environment: '○', safety: '△', rent: '○', recommend: '幅広い層' },
    { area: '練馬', access: '○', environment: '◎', safety: '○', rent: '◎', recommend: '初心者・家族' },
    { area: '高円寺', access: '◎', environment: '○', safety: '○', rent: '○', recommend: '若者・クリエイター' },
    { area: '恵比寿', access: '◎', environment: '◎', safety: '◎', rent: '×', recommend: '都会志向' },
    { area: '浅草', access: '○', environment: '○', safety: '○', rent: '○', recommend: '観光好き' },
    { area: '目黒', access: '◎', environment: '◎', safety: '◎', rent: '×', recommend: '快適重視' },
    { area: '荻窪', access: '○', environment: '○', safety: '◎', rent: '○', recommend: '落ち着き重視' },
    { area: '亀戸', access: '○', environment: '○', safety: '○', rent: '◎', recommend: 'コスパ重視' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888',
                                        fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', mr: 1, fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    東京都内でルームシェアをする魅力とは
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    都会の洗練された暮らし、アクセスの良さ、そして多様な人との出会い――<b>東京都内でルームシェア</b>は、ただ家賃を抑えるだけでなく、日常をもっと豊かにしてくれる選択肢です。<br />
                    しかし「どのエリアを選ぶか」によって、あなたの毎日は大きく変わります。<br />
                    本記事では、<b>交通アクセス・周辺環境・治安・家賃・住みやすさ</b>などの視点で、<b>東京都内のおすすめルームシェアエリア10選</b>を徹底解説。エリアごとのリアルな暮らしやすさ・雰囲気も、専門家の視点から詳しくお届けします。
                </Typography>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <img
                        src="/images/blog/share-dining_tokyo-roomshare-area-10.jpg"
                        alt="明るい共有リビングで、ルームメイトたちがリラックスしながら食事や会話を楽しんでいる様子。木製のテーブルや観葉植物、白を基調とした開放感のある空間で、和やかな雰囲気が漂っている。この記事が紹介するルームシェア生活の理想的な一場面。"
                        style={{ width: '100%', display: 'block' }}
                    />
                </Box>
                <Typography variant="body2" color="textSecondary">
                    ※写真はイメージです。実際の物件やエリアにより異なります。
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* エリア選びのポイント */}
            <Box id="area-selection" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    エリア選びで重視したい5つのポイント
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><DirectionsTransitIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>交通アクセスの良さ</b>}
                            secondary="主要エリアへの所要時間や利用できる路線数は、毎日の快適さに直結します。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><HomeIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>周辺環境・生活利便性</b>}
                            secondary="スーパー・コンビニ・飲食店・カフェの充実度は、暮らしやすさの鍵。公園や自然も注目ポイント。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><SecurityIcon color="success" /></ListItemIcon>
                        <ListItemText
                            primary={<b>治安と安全性</b>}
                            secondary="特に女性や初めての一人暮らしの場合、治安や夜道の明るさも確認しましょう。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><AttachMoneyIcon color="warning" /></ListItemIcon>
                        <ListItemText
                            primary={<b>家賃相場とコスパ</b>}
                            secondary="エリアによって家賃相場は大きく異なります。ルームシェアならではのコスパを活かせる場所が◎"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><GroupIcon color="error" /></ListItemIcon>
                        <ListItemText
                            primary={<b>住民層や街の雰囲気</b>}
                            secondary="街ごとにカルチャーや住民の層も違います。自分に合った雰囲気かも大切な判断軸です。"
                        />
                    </ListItem>
                </List>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* エリア紹介 */}
            <Box id="area-list" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    おすすめルームシェアエリア10選
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    専門家目線でピックアップした、特徴ある10エリアを詳しくご紹介します。
                </Typography>
                <Grid container spacing={3}>
                    {areaData.map((area) => (
                        <Grid item xs={12} sm={6} key={area.id}>
                            <Card sx={{ maxWidth: 380, minHeight: 410, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Avatar sx={{ bgcolor: '#1976d2', mr: 1 }}>{area.icon}</Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{area.name}</Typography>
                                    </Box>
                                    <Box sx={{ borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                                        <img
                                            src={area.image}
                                            alt={area.imageAlt}
                                            style={{ width: '100%', display: 'block', height: 120, objectFit: 'cover' }}
                                        />
                                    </Box>
                                    <List dense>
                                        {area.points.map((point, idx) => (
                                            <ListItem key={idx} sx={{ pl: 0 }}>
                                                <ListItemIcon>{point.icon}</ListItemIcon>
                                                <ListItemText primary={point.label} />
                                            </ListItem>
                                        ))}
                                    </List>
                                    <Box sx={{ mt: 1 }}>
                                        {area.tags.map((tag, i) => (
                                            <Chip key={i} label={tag} color="primary" variant="outlined" size="small" sx={{ mr: 0.5 }} />
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 比較表 */}
            <Box id="comparison" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    主要エリア徹底比較表
                </Typography>
                <Paper sx={{ p: 2, overflowX: 'auto', borderRadius: 2, mb: 2 }}>
                    <Grid container spacing={1} sx={{ fontWeight: 'bold', mb: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} sm={2}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>エリア</Typography></Grid>
                        <Grid item xs={1}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>アクセス</Typography></Grid>
                        <Grid item xs={2}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>環境</Typography></Grid>
                        <Grid item xs={2}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>治安</Typography></Grid>
                        <Grid item xs={1}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>家賃</Typography></Grid>
                        <Grid item xs={3} sm={2}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>おすすめ層</Typography></Grid>
                    </Grid>
                    {areaComparison.map((row, idx) => (
                        <Grid container spacing={1} key={idx} sx={{ borderBottom: idx < areaComparison.length - 1 ? '1px solid #f0f0f0' : 'none', py: 1 }}>
                            <Grid item xs={3} sm={2}><Typography variant="body2">{row.area}</Typography></Grid>
                            <Grid item xs={1}><Typography variant="body2">{row.access}</Typography></Grid>
                            <Grid item xs={2}><Typography variant="body2">{row.environment}</Typography></Grid>
                            <Grid item xs={2}><Typography variant="body2">{row.safety}</Typography></Grid>
                            <Grid item xs={1}><Typography variant="body2">{row.rent}</Typography></Grid>
                            <Grid item xs={3} sm={2}><Typography variant="body2">{row.recommend}</Typography></Grid>
                        </Grid>
                    ))}
                </Paper>
                <Typography variant="body2" color="textSecondary">
                    ※「◎」…非常に優れる、「○」…良い、「△」…やや劣る、「×」…高め/注意点あり
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* アドバイス */}
            <Box id="advice" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    専門家のアドバイスと注意点
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <StarIcon fontSize="small" sx={{ verticalAlign: 'middle', color: '#ffb300', mr: 1 }} />
                        <b>エリアごとに暮らしの「雰囲気」は大きく異なります。</b>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        例えば下北沢や高円寺は、音楽・アートなどカルチャー好きな人にぴったり。一方、中目黒や目黒、恵比寿は落ち着いた大人の雰囲気や洗練された生活を求める人向けです。北千住・練馬・亀戸などは、コスパやアクセス重視で幅広い層におすすめ。
                    </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', color: '#2e7d32', mr: 1 }} />
                        <b>治安や夜道、駅から物件までの道のりも必ず現地確認を！</b>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        いくら人気エリアでも、駅から遠かったり夜道が暗い場合は注意が必要です。特に女性や初めて上京する方は、<b>必ず昼と夜の雰囲気をチェック</b>しましょう。
                    </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <AttachMoneyIcon fontSize="small" sx={{ verticalAlign: 'middle', color: '#ffb300', mr: 1 }} />
                        <b>家賃の目安は、各エリアの「相場」＋ルームシェアならではの割安さを活用</b>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        恵比寿や中目黒など家賃相場が高い場所でも、ルームシェアなら都心生活が現実的に。逆に、練馬や亀戸のようなコスパ重視エリアなら、個室でも負担が少なく快適に過ごせます。
                    </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <GroupIcon fontSize="small" sx={{ verticalAlign: 'middle', color: '#1976d2', mr: 1 }} />
                        <b>ルームシェアは「人」も非常に大事！</b>
                    </Typography>
                    <Typography variant="body2">
                        エリアだけでなく、<b>同居人との相性や生活スタイル</b>も重要。入居前にしっかり話し合い、「暮らし方」や「ルール」を明確にすることが、快適なシェア生活のポイントです。
                    </Typography>
                </Box>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <img
                        src="/images/blog/share-living_tokyo-roomshare-area-10.jpg"
                        alt="共用リビングで複数のルームメイトが談笑しながらリラックスした時間を過ごしている様子。ナチュラルな木目調の家具や、明るい自然光が差し込む室内の温かな空気感が伝わる。"
                        style={{ width: '100%', display: 'block' }}
                    />
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    東京都内でルームシェア生活を始めるなら、<b>「どこに住むか」</b>があなたの毎日を左右します。<br />
                    本記事で紹介した10エリアは、<b>交通アクセス・生活環境・治安・コスパ・雰囲気</b>など、様々なニーズに応える個性派ぞろい。自分のライフスタイルや働き方、趣味に合った場所を選ぶことで、<b>ルームシェアの楽しさと安心</b>を最大限に味わえます。<br />
                    <br />
                    複数の物件やエリアを見比べ、現地見学やルームメイトとの相性も大切に。<br />
                    <b>あなたらしい東京生活の第一歩</b>、ぜひ理想のエリアから始めてみてください。
                </Typography>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <img
                        src="/images/blog/share-balcony_tokyo-roomshare-area-10.jpg"
                        alt="日差しの差し込むバルコニーで、ルームメイト同士がコーヒーカップを手にリラックスしている様子。グリーンの植物と青空、心地よい開放感の中で和やかに過ごすひととき。"
                        style={{ width: '100%', display: 'block' }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'tokyo-roomshare-area-10',
    slug: 'tokyo-roomshare-area-10',
    title: '東京都内のおすすめルームシェアエリア10選！交通アクセス・生活環境を徹底比較',
    excerpt: 'エリア別の特徴\n交通アクセスの良さ\n周辺環境\n治安と安全性\n家賃と住みやすさ',
    coverImage: '/images/blog/cover-tokyo-roomshare-area-10_tokyo-roomshare-area-10.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目調の家具や観葉植物が配され、和やかな雰囲気の中で快適な共同生活を楽しむ様子が伝わる。',
    publishedAt: '2024-06-17T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '東京生活', '住まい選び'],
    tags: ['ルームシェア', 'エリア比較', '東京', '家賃', '治安'],
    author: {
        name: '中村 拓也',
        avatar: '/images/avatars/nakamura.jpg',
        bio: '都内の住まい事情を中心に執筆するライター。賃貸・シェアハウス・ルームシェア経験を活かし、リアルな視点で情報発信中。',
        profilePicture: '/images/avatars/nakamura.jpg'
    },
    viewCount: 1250,
    contentComponent: PostContent
};

export default post;